import { AsyncProps } from "react-select/async";
import { AsyncSelectInput } from "./AsyncSelectInput";
import { productsHttp } from "../../../module/products/service/products-http";
import { config } from "../../../config";
import { useSelector } from "react-redux";
import * as authSelectors from "../../../module/auth/authSelectors";
import { ProductListItem } from "../../../module/products/models/product";
import { useFormContext } from "react-hook-form";

type Props = {
  name: string;
  classification: string;
  onChange?: (value: any) => void;
  label?: string;
  helperText?: string;
  filterProductsFunction?: (products: ProductListItem[]) => ProductListItem[];
} & Omit<AsyncProps<any, any, any>, "defaultOptions">;

export const SelectProductInput = ({
  name,
  classification,
  filterProductsFunction,
  label = "Product",
  helperText,
  ...rest
}: Props) => {
  const { watch } = useFormContext();
  const selectedProductId = watch(name);
  const organization = useSelector(authSelectors.selectedOrganizationKeyName);

  const retrieveAsyncOptions = async (inputValue: string) => {
    try {
      const products = await productsHttp.getProductList_({
        filters: {
          classification,
          ...(inputValue ? { name: inputValue } : {}),
        },
        url: `${config.apiEndpoint}/organizations/${organization}/shop-products`,
      });

      // GET products returns paginated result, so we need to check if the selected product is in the list
      // If it is not, we need to fetch the product from the API
      if (selectedProductId) {
        const selectedProduct = products.data.find(
          (p) => p.id === selectedProductId
        );
        if (!selectedProduct) {
          const product = await productsHttp.getProduct(
            organization,
            selectedProductId
          );
          if (product) {
            products.data.push({
              categoryName: product.categoryName,
              id: product.id,
              name: product.name,
              title: product.title,
              description: product.description,
              imageUrl: product?.imageUrl || "",
              price: {
                amount: product.priceAmount,
                currency: product.priceCurrency,
              },
            });
          }
        }
      }

      const _products = filterProductsFunction
        ? filterProductsFunction(products.data)
        : products.data;

      const options = _products
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((product) => ({
          label: product.name,
          value: product.id,
        }));

      options.unshift({
        label: "None",
        value: "",
      });

      return options;
    } catch (error) {
      return [];
    }
  };

  return (
    <AsyncSelectInput
      name={name}
      retrieveAsyncOptions={retrieveAsyncOptions}
      label={label}
      helperText={helperText}
      {...rest}
    />
  );
};
